'use client'

import React, { memo, useCallback } from 'react'
import Link from 'next/link'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { QuantityInput } from '@/components/ui/quantity-input'
import { formatPrice } from '@/lib/utils'
import { S3Image } from '@/components/S3Image'
import { useCartUI } from '@/context/cart-ui-context'

interface CartItem {
  id: string
  name: string
  price: number
  quantity: number
  image?: string
  stock?: number // ✅ NUEVO: para limitar cantidad en el carrito lateral
  slug?: string
}

interface CartItemProps {
  item: CartItem
  onRemove: (id: string) => void
  onUpdateQuantity: (id: string, quantity: number) => void
}

const CartItemComponent = ({
  item,
  onRemove,
  onUpdateQuantity,
}: CartItemProps) => {
  const { closeCart } = useCartUI()

  const handleRemove = useCallback(() => {
    onRemove(item.id)
  }, [item.id, onRemove])

  // ✅ max dinámico: si hay stock, no dejamos pasar ese límite
  // (si stock viene 0 o negativo, dejamos max=1 para no romper el input)
  const maxAllowed =
    typeof item.stock === 'number' && Number.isFinite(item.stock)
      ? item.stock
      : 99

  const handleQuantityChange = useCallback(
    (quantity: number) => {
      // 🔒 clamp para que nunca se mande una cantidad > stock
      const next = Math.min(Math.max(1, quantity), maxAllowed)
      onUpdateQuantity(item.id, next)
    },
    [item.id, onUpdateQuantity, maxAllowed]
  )

  const imageContent = item.image ? (
    <S3Image
      src={item.image}
      alt={item.name}
      width={64}
      height={64}
      className='w-full h-full object-cover'
    />
  ) : (
    <div className='w-full h-full flex items-center justify-center text-gray-400 text-xs'>
      Sin imagen
    </div>
  )

  return (
    <div className='flex items-center gap-4 py-4 border-b border-gray-200 last:border-b-0'>
      {/* IMAGE */}
      <div className='w-16 h-16 rounded-xl overflow-hidden bg-[#F6F6F3] flex-shrink-0'>
        {item.slug ? (
          <Link
            href={`/productos/${item.slug}`}
            onClick={closeCart}
            className='block w-full h-full'
          >
            {imageContent}
          </Link>
        ) : (
          imageContent
        )}
      </div>

      {/* INFO */}
      <div className='flex-1 min-w-0'>
        <h3 className='text-sm font-allround font-medium text-[#7D744F] truncate'>
          {item.slug ? (
            <Link href={`/productos/${item.slug}`} onClick={closeCart}>
              {item.name}
            </Link>
          ) : (
            item.name
          )}
        </h3>

        <p className='text-sm font-allround font-semibold text-[#8A7E55] mt-0.5'>
          {formatPrice(item.price)}
        </p>
      </div>

      {/* ACTIONS */}
      <div className='flex items-center gap-2'>
        <QuantityInput
          value={item.quantity}
          onChange={handleQuantityChange}
          min={1}
          max={maxAllowed} // ✅ antes 99 fijo
        />

        <Button
          variant='ghost'
          size='icon'
          onClick={handleRemove}
          className='
            text-gray-400 hover:text-red-500
            rounded-full p-1
            hover:bg-red-50 transition-all
          '
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
    </div>
  )
}

export const CartItem = memo(CartItemComponent)
